import { api } from './api';

/**
 * E1 — API call groups for the website, mirroring
 * frontend/customer/src/api/endpoints.ts's real, already-working call
 * shapes exactly (same backend, same routes — no new backend endpoints
 * invented for anything this file covers except the new shop/commerce
 * group at the bottom, which is genuinely new backend work added this
 * phase — see backend/commerce_routes.py).
 */

// ── Auth ──────────────────────────────────────────────────────────────────────

export const authApi = {
  // Registration is not exposed by the customer portal (existing
  // customers only) but IS needed here — this is the same, unmodified
  // backend/auth_routes.py::register() endpoint the mobile app already
  // uses. Matches backend/models.py::UserRegister exactly: email,
  // password (min 12 chars), name, optional passcode.
  async register(email, password, name) {
    const r = await api.post('/auth/register', { email, password, name });
    return r.data;
  },

  async login(email, password) {
    const r = await api.post('/auth/login', { email, password });
    return r.data;
  },

  async verifyMfa(pre_mfa_token, code) {
    const r = await api.post('/auth/mfa/verify', { pre_mfa_token, code });
    return r.data;
  },

  async me() {
    const r = await api.get('/auth/me');
    return r.data;
  },

  async refresh() {
    return api.post('/auth/refresh');
  },

  async logout() {
    return api.post('/auth/logout');
  },

  async forgotPassword(email) {
    return api.post('/auth/forgot-password', { email });
  },

  async resetPassword(token, new_password) {
    return api.post('/auth/reset-password', { token, new_password });
  },

  async changePassword(current_password, new_password) {
    return api.post('/auth/change-password', { current_password, new_password });
  },

  // Same real backend endpoint the mobile app's in-app deletion and the
  // customer portal's "Danger Zone" (added in the prior G2 phase) both
  // already call — never a fake client-only flow.
  async deleteAccount() {
    return api.delete('/auth/account/v2');
  },
};

// ── Dashboard ─────────────────────────────────────────────────────────────────

export const dashboardApi = {
  async get() {
    const r = await api.get('/mobile/dashboard');
    return r.data;
  },
};

// ── Homes ─────────────────────────────────────────────────────────────────────

export const homesApi = {
  async list() {
    const r = await api.get('/mobile/homes');
    return r.data.homes;
  },
  async get(id) {
    const r = await api.get(`/mobile/homes/${id}`);
    return r.data.home;
  },
};

// ── Devices ───────────────────────────────────────────────────────────────────

export const devicesApi = {
  async list(room_id) {
    const r = await api.get('/mobile/devices', { params: room_id ? { room_id } : {} });
    return r.data.devices;
  },
  async get(id) {
    const r = await api.get(`/mobile/devices/${id}`);
    return r.data.device;
  },
  async control(id, action, params) {
    return api.post(`/mobile/devices/${id}/control`, { action, ...params });
  },
};

// ── Cameras ───────────────────────────────────────────────────────────────────

export const camerasApi = {
  async list() {
    const r = await api.get('/mobile/cameras');
    return r.data.cameras;
  },
  async get(id) {
    const r = await api.get(`/mobile/cameras/${id}`);
    return r.data.camera;
  },
  async getStream(id) {
    const r = await api.get(`/mobile/cameras/${id}/stream`);
    return r.data;
  },
};

// ── Scenes ────────────────────────────────────────────────────────────────────

export const scenesApi = {
  async list() {
    const r = await api.get('/mobile/scenes');
    return r.data.scenes;
  },
  async execute(id) {
    return api.post(`/mobile/scenes/${id}/execute`);
  },
};

// ── Automations ───────────────────────────────────────────────────────────────

export const automationsApi = {
  async list() {
    const r = await api.get('/mobile/automations');
    return r.data.automations;
  },
  async toggle(id) {
    const r = await api.patch(`/mobile/automations/${id}/toggle`);
    return r.data;
  },
  async runNow(id) {
    return api.post(`/mobile/automations/${id}/run`);
  },
};

// ── Energy ────────────────────────────────────────────────────────────────────

export const energyApi = {
  async getSummary(period = 'week') {
    const r = await api.get('/mobile/energy/summary', { params: { period } });
    return r.data;
  },
};

// ── Public marketing-site content (NEW this phase — backend/public_routes.py) ──
// Real, thin routes on the canonical backend replacing the legacy
// website backend's equivalents — same form, same purpose, no separate
// backend involved.

export const contactApi = {
  async submit(data) {
    return api.post('/public/contact', data);
  },
};

export const planRequestsApi = {
  async submit(data) {
    return api.post('/public/plan-requests', data);
  },
};

export const projectsApi = {
  async getAll() {
    const r = await api.get('/public/projects');
    return r.data.projects;
  },
};

// ── Compatible devices / protocols ─────────────────────────────────────────────
// A real, public, unauthenticated backend endpoint that already existed
// before this phase (backend/device_routes.py::GET /mobile/protocols) —
// returns the live provider registry, so this page never goes stale as
// new providers are added.

export const protocolsApi = {
  async list() {
    const r = await api.get('/mobile/protocols');
    return r.data.protocols;
  },
};

// ── Gateways ──────────────────────────────────────────────────────────────────
// No dedicated customer-facing gateway screen exists anywhere in this
// ecosystem yet (mobile or web) — this is a genuinely new UI surface,
// built here for the first time, against the existing
// backend/gateway_routes.py::GET /mobile/gateways endpoint.

export const gatewaysApi = {
  async list() {
    const r = await api.get('/mobile/gateways');
    return r.data.gateways;
  },
};

// ── Profile ───────────────────────────────────────────────────────────────────

export const profileApi = {
  async get() {
    const r = await api.get('/mobile/profile');
    return r.data;
  },
  async update(data) {
    const r = await api.put('/mobile/profile', data);
    return r.data;
  },
};

// ── Shop / Commerce (NEW this phase — backend/commerce_routes.py) ─────────────
// Foundation only: public catalogue browsing + a customer's own order
// history. No payment processing, no checkout, no supplier integration —
// per the explicit E1 scope boundary.

export const shopApi = {
  async listProducts(params) {
    const r = await api.get('/shop/products', { params });
    return r.data.products;
  },
  async getProduct(id) {
    const r = await api.get(`/shop/products/${id}`);
    return r.data.product;
  },
  async listMyOrders() {
    const r = await api.get('/shop/orders');
    return r.data.orders;
  },
};
